import React from 'react';
import { Link } from 'react-router-dom';
import { Database, Rocket, Users } from 'lucide-react';

/**
 * Two blocks:
 *  A — Why Vyvre (3-column comparison)
 *  B — For Brands / For Agencies split
 */
export const WhyUsSection = () => (
  <>
    {/* A. Comparison — premium dark section */}
    <section className="relative bg-[#0A0A0A] py-24 md:py-32 overflow-hidden" data-testid="why-us-section">
      {/* Soft ambient glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] rounded-full opacity-40 pointer-events-none blur-3xl"
        style={{ background: 'radial-gradient(closest-side, rgba(255,69,26,0.10), transparent 70%)' }}
      />
      <div
        className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none blur-3xl"
        style={{ background: 'radial-gradient(closest-side, rgba(201,151,74,0.10), transparent 70%)' }}
      />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#FF451A] mb-5">
            Where we fit
          </div>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]"
            data-testid="why-us-headline"
          >
            Not Just a Database.
            <br />
            <span className="text-[#FF451A]">Not Just a Tool.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5 items-stretch">
          <CompareCard
            icon={Database}
            label="Discovery Platforms"
            sub="Qoruz, Hashfame, etc."
            body="Show you who to work with. You still manage everything yourself over WhatsApp and Excel."
          />
          <CompareCard
            icon={Rocket}
            label="Vyvre"
            sub="You are here"
            body="We find the creators AND manage the entire campaign — with a platform your brand team can see in real time. No WhatsApp chaos."
            highlighted
          />
          <CompareCard
            icon={Users}
            label="Traditional Agencies"
            sub="Legacy players"
            body="Manage campaigns but give brands zero visibility. You get a report at the end and hope for the best."
          />
        </div>
      </div>

      {/* Animated gradient border — used on the highlighted card. */}
      <style>{`
        @keyframes shn-border-spin {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .shn-glow-card::before {
          content: '';
          position: absolute;
          inset: -2px;
          border-radius: 1.05rem;
          padding: 2px;
          background: linear-gradient(120deg, #FF451A, #C9974A, #FF451A, #C9974A, #FF451A);
          background-size: 300% 300%;
          animation: shn-border-spin 3s linear infinite;
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          pointer-events: none;
          z-index: 0;
        }
        @media (prefers-reduced-motion: reduce) {
          .shn-glow-card::before { animation: none; }
        }
      `}</style>
    </section>

    {/* B. For Brands / For Agencies split */}
    <section className="bg-[#FAFAFA] border-y border-black/[0.06] py-20 md:py-28" data-testid="segments-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <SegmentCard
            id="for-brands"
            eyebrow="For Brands"
            heading="Brief us, we execute, you watch it happen."
            bullets={[
              'Creator discovery across 19,000+ profiles',
              'Real-time campaign progress dashboard',
              'Deliverable approvals without leaving the platform',
              'Product seeding with address collection',
              'Branded campaign reports',
            ]}
            ctaLabel="Book a Demo"
            ctaVariant="orange"
            testid="segment-brands"
          />
          <SegmentCard
            id="for-agencies"
            eyebrow="For Agencies"
            heading="Run multiple client campaigns without losing your mind."
            bullets={[
              'Manage campaigns for multiple brands',
              'Full AE, CM and TM workflow',
              'Creator roster management',
              'Client-facing dashboards',
              'White-label reporting (coming soon)',
            ]}
            ctaLabel="Talk to Us"
            ctaVariant="outline"
            testid="segment-agencies"
          />
        </div>
      </div>
    </section>
  </>
);

const CompareCard = ({ icon: Icon, label, sub, body, highlighted }) => (
  <div
    className={`relative rounded-2xl p-7 md:p-8 flex flex-col transition-all ${
      highlighted
        ? 'shn-glow-card bg-[#141414] shadow-[0_20px_80px_rgba(255,69,26,0.30),0_10px_40px_rgba(201,151,74,0.20)] md:-translate-y-2'
        : 'bg-[#141414] border border-[#2A2A2A] hover:border-[#3A3A3A]'
    }`}
    data-testid={highlighted ? 'compare-strawhat' : undefined}
  >
    <div className="relative z-10">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
        highlighted ? 'bg-[#FF451A]' : 'bg-white/[0.05]'
      }`}>
        <Icon size={22} className={highlighted ? 'text-white' : 'text-[#A1A1AA]'} />
      </div>
      <div className={`text-[10px] font-mono uppercase tracking-[0.2em] mb-2 ${
        highlighted ? 'text-[#C9974A]' : 'text-[#A1A1AA]/70'
      }`}>
        {sub}
      </div>
      <div className="text-xl font-semibold text-white mb-3 tracking-tight">{label}</div>
      <div className="text-sm text-[#A1A1AA] leading-relaxed">{body}</div>
    </div>
  </div>
);

const SegmentCard = ({ id, eyebrow, heading, bullets, ctaLabel, ctaVariant, testid }) => (
  <div
    id={id}
    className="bg-white border border-black/[0.08] rounded-2xl p-8 md:p-10 flex flex-col hover:shadow-[0_20px_60px_rgba(15,15,15,0.08)] transition-shadow"
    data-testid={testid}
  >
    <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF451A] mb-3">
      {eyebrow}
    </div>
    <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0F0F0F] mb-5 leading-snug">
      {heading}
    </h3>
    <ul className="space-y-2.5 mb-8 flex-1">
      {bullets.map((b) => (
        <li key={b} className="text-sm text-[#4B5563] flex items-start gap-2.5 leading-relaxed">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FF451A] shrink-0" />
          {b}
        </li>
      ))}
    </ul>
    <Link
      to="/demo"
      className={`self-start px-6 py-2.5 rounded-full text-sm font-semibold transition-colors inline-flex items-center ${
        ctaVariant === 'orange'
          ? 'bg-[#FF451A] text-white hover:bg-[#E63E17] shadow-[0_6px_24px_rgba(255,69,26,0.28)]'
          : 'border border-black/15 text-[#0F0F0F] hover:bg-black/[0.03]'
      }`}
      data-testid={`${testid}-cta`}
    >
      {ctaLabel}
    </Link>
  </div>
);

export default WhyUsSection;
